import { FormEvent, useEffect, useState } from 'react';
import { api, ApiError, type UserRole } from '../api';
import { AppShell } from '../components/AppShell';
import { useAuthStore } from '../auth';

type Member = {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  isPlatformAdmin: boolean;
  lastLoginAt: string | null;
};

type Invite = {
  id: string;
  email: string;
  role: UserRole;
  expiresAt: string;
  createdAt: string;
  invitedBy: { id: string; name: string; email: string } | null;
};

export function MembersPage() {
  const me = useAuthStore((s) => s.me);
  const canInvite = me?.user.role === 'OWNER' || me?.user.role === 'ADMIN';
  const [members, setMembers] = useState<Member[] | null>(null);
  const [invites, setInvites] = useState<Invite[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [newInvite, setNewInvite] = useState<{
    email: string;
    token: string;
  } | null>(null);

  async function load() {
    try {
      setErr(null);
      const [m, i] = await Promise.all([
        api.listMembers(),
        api.listInvitations(),
      ]);
      setMembers(m);
      setInvites(i);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed_to_load');
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Members</h1>
            <p className="mt-1 text-muted">
              Everyone who can sign in to your organization, plus
              outstanding invitations. Roles: <strong>Owner</strong>{' '}
              (everything, including billing), <strong>Admin</strong>{' '}
              (everything except billing), <strong>Operator</strong>{' '}
              (game control), <strong>Viewer</strong> (read-only).
            </p>
          </div>
          {canInvite && !showForm && !newInvite && (
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
            >
              + Invite teammate
            </button>
          )}
        </div>

        {err && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {err}
          </div>
        )}

        {newInvite && (
          <NewInviteCard
            data={newInvite}
            onDone={async () => {
              setNewInvite(null);
              await load();
            }}
          />
        )}

        {showForm && (
          <InviteForm
            onDone={async (token, email) => {
              setShowForm(false);
              setNewInvite({ email, token });
              await load();
            }}
            onCancel={() => setShowForm(false)}
          />
        )}

        {invites && invites.length > 0 && (
          <div>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted">
              Pending invitations ({invites.length})
            </h2>
            <div className="overflow-hidden rounded-2xl border border-border">
              <table className="w-full divide-y divide-border text-sm">
                <thead className="bg-surface text-xs uppercase tracking-wider text-muted">
                  <tr>
                    <th className="px-4 py-3 text-left">Email</th>
                    <th className="px-4 py-3 text-left">Role</th>
                    <th className="px-4 py-3 text-left">Expires</th>
                    <th className="px-4 py-3 text-left">Invited by</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-card">
                  {invites.map((i) => (
                    <tr key={i.id}>
                      <td className="px-4 py-3">{i.email}</td>
                      <td className="px-4 py-3 text-muted">
                        {i.role.toLowerCase()}
                      </td>
                      <td className="px-4 py-3 text-muted">
                        {new Date(i.expiresAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-muted">
                        {i.invitedBy?.name ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canInvite && (
                          <button
                            onClick={async () => {
                              if (!confirm(`Revoke invitation for ${i.email}?`)) return;
                              await api.revokeInvitation(i.id);
                              await load();
                            }}
                            className="text-xs text-muted hover:text-red-400"
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted">
            Members ({members?.length ?? 0})
          </h2>
          {!members ? (
            <div className="text-muted">Loading…</div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-border">
              <table className="w-full divide-y divide-border text-sm">
                <thead className="bg-surface text-xs uppercase tracking-wider text-muted">
                  <tr>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">Email</th>
                    <th className="px-4 py-3 text-left">Role</th>
                    <th className="px-4 py-3 text-left">Last login</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-card">
                  {members.map((m) => (
                    <tr key={m.id}>
                      <td className="px-4 py-3">
                        {m.name}
                        {m.isPlatformAdmin && (
                          <span className="ml-2 rounded-full border border-brand/30 bg-brand/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-light">
                            Platform
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted">{m.email}</td>
                      <td className="px-4 py-3 text-muted">
                        {m.role.toLowerCase()}
                      </td>
                      <td className="px-4 py-3 text-muted">
                        {m.lastLoginAt
                          ? new Date(m.lastLoginAt).toLocaleString()
                          : 'never'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function InviteForm({
  onDone,
  onCancel,
}: {
  onDone: (token: string, email: string) => void;
  onCancel: () => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('OPERATOR');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await api.createInvitation({ email, role });
      onDone(r.token, email);
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? humanize(e.message)
          : 'failed_to_invite',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="card space-y-4">
      <h2 className="text-lg font-semibold">Invite a teammate</h2>
      <div>
        <label className="label">Email</label>
        <input
          className="input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <div>
        <label className="label">Role</label>
        <select
          className="input"
          value={role}
          onChange={(e) => setRole(e.target.value as UserRole)}
        >
          <option value="OPERATOR">Operator — game control + scoring</option>
          <option value="STATS_KEEPER">Stats Keeper — logs player stats only</option>
          <option value="ADMIN">Admin — everything except billing</option>
          <option value="VIEWER">Viewer — read-only dashboards</option>
        </select>
      </div>
      {err && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {err}
        </div>
      )}
      <div className="flex gap-2">
        <button className="btn btn-primary" disabled={busy}>
          {busy ? 'Inviting…' : 'Send invitation'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function NewInviteCard({
  data,
  onDone,
}: {
  data: { email: string; token: string };
  onDone: () => void;
}) {
  const link = `${window.location.origin}/invite/${data.token}`;
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  return (
    <div className="card border-brand/40 bg-brand/5">
      <div className="text-xs uppercase tracking-wider text-brand-light">
        Invitation created
      </div>
      <h3 className="mt-1 text-lg font-semibold">
        Share this link with {data.email}
      </h3>
      <p className="mt-2 text-sm text-muted">
        Email sending isn't wired up yet — copy this link and send it to
        them however you like. It expires in 7 days.
      </p>
      <div className="mt-4 flex gap-2">
        <input
          readOnly
          className="input flex-1 font-mono text-xs"
          value={link}
          onClick={(e) => (e.target as HTMLInputElement).select()}
        />
        <button className="btn btn-primary shrink-0" onClick={copy}>
          {copied ? 'Copied!' : 'Copy link'}
        </button>
      </div>
      <button onClick={onDone} className="btn btn-secondary mt-4">
        Done
      </button>
    </div>
  );
}

function humanize(code: string): string {
  switch (code) {
    case 'email_already_a_user':
      return 'That email already belongs to a member of this org.';
    case 'invitation_already_pending':
      return 'An invitation for that email is already pending.';
    case 'only_owners_and_admins_can_invite':
      return 'Only owners and admins can invite teammates.';
    default:
      return code;
  }
}
